import { Desktop, FolderOpen, Plus, UploadSimple } from '@phosphor-icons/react';
import { CloudProjects } from './CloudProjects';
import { useAuth } from '../auth/session';
import { signInConfigured } from '../lib/supabase';
import { pickTextFile } from '../io/download';
import { readProjectFile } from '../io/project-file';
import { useEffect, useState } from 'react';
import p from './Projects.module.css';
import { riversideExample } from '../example/riverside';
import { savedLibrary } from '../state/actions';
import { DEFAULT_SETTINGS, type ProjectDoc } from '../model/types';
import { db, newId, type StoredProject } from '../state/db';
import { useStore } from '../state/store';
import { Buildings } from '@phosphor-icons/react';
import { Button } from '../ui/kit';
import { TopBar } from '../workspace/TopBar';

const blank = (): ProjectDoc => ({
  schemaVersion: 1,
  meta: { name: 'Untitled project' },
  // start from the values this engineer has saved before
  settings: { ...DEFAULT_SETTINGS, referenceValues: savedLibrary() },
  substances: [],
  areas: [],
  sources: [],
  equipment: [],
  objects: [],
});

async function open(doc: ProjectDoc, id = newId()) {
  await db.projects.put({ id, name: doc.meta.name, updatedAt: Date.now(), doc });
  localStorage.setItem('extent:last', id);
  useStore.getState().openProject(id, doc);
}

export function ProjectsScreen() {
  const auth = useAuth();
  const [projects, setProjects] = useState<StoredProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    db.projects.orderBy('updatedAt').reverse().toArray().then(setProjects).catch(() => setError('This browser refused local storage, so projects cannot be saved here. Check private-window or site-data settings.'));
  }, []);
  const fmt = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <div className={p.page}>
      <TopBar />
      <main className={p.main}>
        <div className={p.head}>
          <div>
            <h1>Projects</h1>
            <p className={p.lede}>Hazardous area classification to IEC 60079-10-1:2020.</p>
          </div>
          <div className={p.actions}>
            <Button icon={<UploadSimple size={15} />} onClick={async () => {
              const f = await pickTextFile('.json,application/json');
              if (!f) return;
              const r = readProjectFile(f.text);
              if ('error' in r) setError(`${f.name}: ${r.error}`);
              else await open(r.doc);
            }}>Import project file</Button>
            <Button onClick={() => open(riversideExample())} icon={<FolderOpen size={15} />}>Open the example project</Button>
            <Button variant="primary" onClick={() => open(blank())} icon={<Plus size={15} />}>New project</Button>
          </div>
        </div>
        {error && <p className={p.error} role="alert">{error}</p>}

        {signInConfigured && auth.state === 'signed-in' && <CloudProjects localDoc={null} />}
        {signInConfigured && auth.state === 'signed-out' && (
          <section className={p.panel}>
            <h2><Buildings size={18} /> Work with your firm</h2>
            <p className={p.panelLede}>
              Sign in to keep projects in your organisation, where colleagues can open them and every
              issued revision is kept. Projects below stay in this browser either way.
            </p>
            <Button variant="primary" onClick={() => useStore.getState().setSigningIn(true)}>Sign in</Button>
          </section>
        )}

        <h2 className={p.localHead}><Desktop size={16} /> In this browser</h2>
        {projects && projects.length === 0 && (
          <div className={p.empty}>
            <h3>Nothing saved in this browser</h3>
            <p>Start with the example: a solvent store with a pump house, tank bund and drum store, to see how sources, zones and equipment checks work. Its data is invented.</p>
          </div>
        )}
        {projects && projects.length > 0 && (
          <table className={p.table}>
            <thead><tr><th>Name</th><th>Client</th><th>Site</th><th className={p.num}>Sources</th><th>Last changed</th></tr></thead>
            <tbody>
              {projects.map((x) => (
                <tr key={x.id} tabIndex={0} onClick={() => open(x.doc, x.id)} onKeyDown={(e) => { if (e.key === 'Enter') void open(x.doc, x.id); }}>
                  <td className={p.name}>{x.name}</td><td>{x.doc.meta.client ?? '—'}</td><td>{x.doc.meta.site ?? '—'}</td>
                  <td className={p.num}>{x.doc.sources.length}</td><td>{fmt.format(x.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}
